import type { HotelFilterPillData } from '@apps/main/new-desktop/modules/hotelFilters';
import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';

import type { IngredientId } from '@libs/trip-planner-interfaces';
import type { TagData } from '@libs/ui';
import { exhaustiveGuard } from '@libs/utils';

import type { ItineraryAccommodationSegment } from './accommodation';
import type { ItineraryFerrySegment } from './ferry';
import type { ItineraryFlightSegment } from './flight';
import { ItinerarySegmentType } from './itinerarySegmentType';
import type {
  ItineraryEndLocationSegment,
  ItineraryStartLocationSegment,
  LocationSegment,
} from './location';
import type {
  ItineraryNoTransportSegment,
  ItineraryStenaUpsellNoTransportSegment,
  ItineraryUpsellNoTransportSegment,
} from './noTransport';
import type { ItineraryOwnStaySegment } from './ownStay';
import type { ProductCardTopBarType } from './shared';
import type { ItinerarySimpleTransportSegment } from './typeGuards';

export interface ItineraryDateLocationLoaderSegment {
  segmentType: ItinerarySegmentType.DateLocation;
  location: LocationSegment;
  ingredientId: IngredientId;
  numberOfNights?: number;
}

interface ItineraryTransportLoaderSegmentBase {
  ingredientId: IngredientId;
  isLastSegment?: boolean;
}

export type ItineraryNoTransportLoaderSegment = ItineraryNoTransportSegment;

export type ItinerarySimpleTransportLoaderSegment =
  ItinerarySimpleTransportSegment;

export interface ItineraryFlightLoaderSegment
  extends ItineraryTransportLoaderSegmentBase {
  segmentType: ItinerarySegmentType.Flight;
  suggestionType: SuggestionType | undefined;
  filtersPills: TagData[];
  barType?: ProductCardTopBarType;
}

export interface ItineraryFerryLoaderSegment
  extends ItineraryTransportLoaderSegmentBase {
  segmentType: ItinerarySegmentType.Ferry;
  suggestionType: SuggestionType | undefined;
  filtersPills: TagData[];
  barType?: ProductCardTopBarType;
}

export type ItineraryOwnStayLoaderSegment = ItineraryOwnStaySegment;

export interface ItineraryAccommodationLoaderSegment {
  segmentType: ItinerarySegmentType.Accommodation;
  barType: ProductCardTopBarType;
  ingredientId: IngredientId;
  ingredientIndex: number;
  filters: HotelFilterPillData[];
}

export interface ItineraryActivityLoaderSegment {
  segmentType: ItinerarySegmentType.Activity;
  barType: undefined;
  ingredientId: IngredientId;
  ingredientIndex: number;
}

export interface ItineraryUpsellLoaderSegment {
  segmentType: ItinerarySegmentType.Upsell;
  ingredientId: IngredientId;
}

export type ItineraryLoaderSegment =
  | ItineraryNoTransportLoaderSegment
  | ItineraryLocationLoaderSegment
  | ItineraryDateLocationLoaderSegment
  | ItinerarySimpleTransportLoaderSegment
  | ItineraryOwnStayLoaderSegment
  | ItineraryBookableProductLoaderSegment
  | ItineraryUpsellLoaderSegment
  | ItineraryStenaUpsellNoTransportSegment
  | ItineraryUpsellNoTransportSegment;

export type ItineraryLocationLoaderSegment =
  | ItineraryStartLocationSegment
  | ItineraryEndLocationSegment;

export type ItineraryBookableProductLoaderSegment =
  | ItineraryAccommodationLoaderSegment
  | ItineraryActivityLoaderSegment
  | ItineraryBookableTransportLoaderSegment;

export type ItineraryBookableTransportLoaderSegment =
  | ItineraryFerryLoaderSegment
  | ItineraryFlightLoaderSegment;

export type ItineraryTransportLoaderSegment =
  | ItineraryBookableTransportLoaderSegment
  | ItinerarySimpleTransportLoaderSegment;

export type ItineraryOwnTransportLoaderSegment =
  | ItinerarySimpleTransportLoaderSegment
  | ItineraryNoTransportLoaderSegment
  | ItineraryUpsellNoTransportSegment
  | ItineraryStenaUpsellNoTransportSegment;

export function isItinerarySimpleTransportLoaderSegment(
  segment: ItineraryLoaderSegment
): segment is ItinerarySimpleTransportLoaderSegment {
  switch (segment.segmentType) {
    case ItinerarySegmentType.OwnStay:
    case ItinerarySegmentType.Accommodation:
    case ItinerarySegmentType.Activity:
    case ItinerarySegmentType.StartLocation:
    case ItinerarySegmentType.EndLocation:
    case ItinerarySegmentType.Ferry:
    case ItinerarySegmentType.Flight:
    case ItinerarySegmentType.NoTransport:
    case ItinerarySegmentType.Upsell:
    case ItinerarySegmentType.UpsellNoTransport:
    case ItinerarySegmentType.StenaUpsellNoTransport:
    case ItinerarySegmentType.DateLocation:
      return false;

    case ItinerarySegmentType.Car:
    case ItinerarySegmentType.Cycle:
    case ItinerarySegmentType.Walk:
      return true;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isItineraryLocationLoaderSegment(
  segment: ItineraryLoaderSegment
): segment is ItineraryLocationLoaderSegment {
  switch (segment.segmentType) {
    case ItinerarySegmentType.OwnStay:
    case ItinerarySegmentType.Accommodation:
    case ItinerarySegmentType.Activity:
    case ItinerarySegmentType.Ferry:
    case ItinerarySegmentType.Flight:
    case ItinerarySegmentType.Car:
    case ItinerarySegmentType.Cycle:
    case ItinerarySegmentType.Walk:
    case ItinerarySegmentType.NoTransport:
    case ItinerarySegmentType.Upsell:
    case ItinerarySegmentType.UpsellNoTransport:
    case ItinerarySegmentType.StenaUpsellNoTransport:
    case ItinerarySegmentType.DateLocation:
      return false;

    case ItinerarySegmentType.StartLocation:
    case ItinerarySegmentType.EndLocation:
      return true;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isItineraryDateLocationLoaderSegment(
  segment: ItineraryLoaderSegment
): segment is ItineraryDateLocationLoaderSegment {
  switch (segment.segmentType) {
    case ItinerarySegmentType.OwnStay:
    case ItinerarySegmentType.Accommodation:
    case ItinerarySegmentType.Activity:
    case ItinerarySegmentType.Ferry:
    case ItinerarySegmentType.Flight:
    case ItinerarySegmentType.Car:
    case ItinerarySegmentType.Cycle:
    case ItinerarySegmentType.Walk:
    case ItinerarySegmentType.NoTransport:
    case ItinerarySegmentType.Upsell:
    case ItinerarySegmentType.UpsellNoTransport:
    case ItinerarySegmentType.StenaUpsellNoTransport:
    case ItinerarySegmentType.StartLocation:
    case ItinerarySegmentType.EndLocation:
      return false;

    case ItinerarySegmentType.DateLocation:
      return true;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isItineraryBookableProductLoaderSegment(
  segment: ItineraryLoaderSegment
): segment is ItineraryBookableProductLoaderSegment {
  switch (segment.segmentType) {
    case ItinerarySegmentType.Accommodation:
    case ItinerarySegmentType.Activity:
    case ItinerarySegmentType.Ferry:
    case ItinerarySegmentType.Flight:
      return true;

    case ItinerarySegmentType.OwnStay:
    case ItinerarySegmentType.StartLocation:
    case ItinerarySegmentType.EndLocation:
    case ItinerarySegmentType.Car:
    case ItinerarySegmentType.Cycle:
    case ItinerarySegmentType.Walk:
    case ItinerarySegmentType.NoTransport:
    case ItinerarySegmentType.Upsell:
    case ItinerarySegmentType.UpsellNoTransport:
    case ItinerarySegmentType.StenaUpsellNoTransport:
    case ItinerarySegmentType.DateLocation:
      return false;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isItineraryAccommodationLoaderSegment(
  segment: ItineraryAccommodationLoaderSegment | ItineraryAccommodationSegment
): segment is ItineraryAccommodationLoaderSegment {
  return !('statusType' in segment);
}

export function isItineraryBookableTransportLoaderSegment(
  segment: ItineraryLoaderSegment
): segment is ItineraryBookableTransportLoaderSegment {
  switch (segment.segmentType) {
    case ItinerarySegmentType.Ferry:
    case ItinerarySegmentType.Flight:
      return true;

    case ItinerarySegmentType.OwnStay:
    case ItinerarySegmentType.Accommodation:
    case ItinerarySegmentType.Activity:
    case ItinerarySegmentType.StartLocation:
    case ItinerarySegmentType.EndLocation:
    case ItinerarySegmentType.Car:
    case ItinerarySegmentType.Cycle:
    case ItinerarySegmentType.Walk:
    case ItinerarySegmentType.NoTransport:
    case ItinerarySegmentType.Upsell:
    case ItinerarySegmentType.UpsellNoTransport:
    case ItinerarySegmentType.StenaUpsellNoTransport:
    case ItinerarySegmentType.DateLocation:
      return false;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isItineraryFlightLoaderSegment(
  segment: ItineraryFlightLoaderSegment | ItineraryFlightSegment
): segment is ItineraryFlightLoaderSegment {
  return !('statusType' in segment);
}

export function isItineraryFerryLoaderSegment(
  segment: ItineraryFerryLoaderSegment | ItineraryFerrySegment
): segment is ItineraryFerryLoaderSegment {
  return !('statusType' in segment);
}
